// Parse trade text from OCR or manual input
export const parseTradeText = (text) => {
  const lines = text.split('\n').map(line => line.trim()).filter(line => line);
  const trades = [];
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    
    // Try to parse different formats
    // Format 1: "12345 2024-01-15 10:30 2024-01-15 10:45 NAS100 Buy 15500.00 15520.00 1.0 0 0 20.00 Win"
    const format1 = /(\d+)\s+([\d-]+\s+[\d:]+)\s+([\d-]+\s+[\d:]+)\s+(\w+)\s+(Buy|Sell)\s+([\d.]+)\s+([\d.]+)\s+([\d.]+)\s+([\d.]+)\s+([\d.]+)\s+([-\d.]+)\s+(Win|Loss)/i;
    
    // Format 2: More flexible parsing
    const format2 = /(\d+).*?(Buy|Sell).*?([\d.]+).*?([\d.]+).*?([-\d.]+)/i;
    
    let match = line.match(format1);
    
    if (match) {
      trades.push({
        tradeId: match[1],
        openDate: new Date(match[2]),
        closeDate: new Date(match[3]),
        symbol: match[4],
        side: match[5],
        entry: parseFloat(match[6]),
        exit: parseFloat(match[7]),
        qty: parseFloat(match[8]),
        fee: parseFloat(match[9]),
        swap: parseFloat(match[10]),
        pnl: parseFloat(match[11]),
        status: match[12],
      });
    } else {
      // Try simpler format
      match = line.match(format2);
      if (match) {
        const side = match[2];
        const entry = parseFloat(match[3]);
        const exit = parseFloat(match[4]);
        const pnl = parseFloat(match[5]);
        
        trades.push({
          tradeId: match[1],
          openDate: new Date(),
          closeDate: new Date(),
          symbol: 'NAS100',
          side: side,
          entry: entry,
          exit: exit,
          qty: 1.0,
          fee: 0,
          swap: 0,
          pnl: pnl,
          status: pnl >= 0 ? 'Win' : 'Loss',
        });
      }
    }
  }
  
  return trades;
};

// Calculate comprehensive analysis including risk metrics
export const calculateAnalysis = (trades, initialBalance = 10000) => {
  if (!trades || trades.length === 0) {
    return {
      totalPnl: 0,
      winRate: 0,
      avgDuration: 0,
      avgPoints: 0,
      avgPnl: 0,
      qtyStd: 0,
      lowPnlCount: 0,
      scalpCount: 0,
      zeroFeeSwap: true,
      totalTrades: 0,
      cumulativePnl: [],
      maxDrawdown: 0,
      maxDrawdownPercent: 0,
      profitFactor: 0,
      sharpeRatio: 0,
      avgRiskReward: 0,
      tradingStyle: 'Unknown',
      consecutiveWins: 0,
      consecutiveLosses: 0,
      bestTrade: null,
      worstTrade: null,
      winningDays: 0,
      losingDays: 0,
    };
  }

  // Basic metrics
  const totalPnl = trades.reduce((sum, t) => sum + (t.pnl || 0), 0);
  const wins = trades.filter(t => t.status === 'Win' || t.pnl >= 0);
  const losses = trades.filter(t => t.status === 'Loss' || t.pnl < 0);
  const winRate = (wins.length / trades.length) * 100;
  
  // Duration analysis
  const durations = trades.map(t => {
    const open = new Date(t.openDate);
    const close = new Date(t.closeDate);
    return (close - open) / (1000 * 60); // minutes
  });
  const avgDuration = durations.reduce((sum, d) => sum + d, 0) / durations.length;
  
  // Points analysis
  const points = trades.map(t => {
    const diff = Math.abs(t.exit - t.entry);
    return diff;
  });
  const avgPoints = points.reduce((sum, p) => sum + p, 0) / points.length;
  
  // P&L analysis
  const avgPnl = totalPnl / trades.length;
  const pnls = trades.map(t => t.pnl);
  const pnlMean = avgPnl;
  const pnlVariance = pnls.reduce((sum, pnl) => sum + Math.pow(pnl - pnlMean, 2), 0) / trades.length;
  const pnlStdDev = Math.sqrt(pnlVariance);
  
  // Quantity standard deviation
  const quantities = trades.map(t => t.qty || 1);
  const qtyMean = quantities.reduce((sum, q) => sum + q, 0) / quantities.length;
  const qtyVariance = quantities.reduce((sum, q) => sum + Math.pow(q - qtyMean, 2), 0) / quantities.length;
  const qtyStd = Math.sqrt(qtyVariance);
  
  // Low P&L and scalp counts
  const lowPnlCount = trades.filter(t => Math.abs(t.pnl) < 10).length;
  const scalpCount = durations.filter(d => d < 5).length;
  
  // Fee and swap analysis
  const zeroFeeSwap = trades.every(t => (t.fee || 0) === 0 && (t.swap || 0) === 0);
  
  // Cumulative P&L for equity curve
  let cumulative = 0;
  const cumulativePnl = trades.map(t => {
    cumulative += t.pnl || 0;
    return cumulative;
  });
  
  // Maximum drawdown calculation (from initial balance)
  let peak = initialBalance;
  let maxDrawdown = 0;
  let maxDrawdownPercent = 0;
  
  // We need to track equity, not just cumulative P&L
  // Equity = initial balance + cumulative P&L
  
  for (let i = 0; i < cumulativePnl.length; i++) {
    const equity = initialBalance + cumulativePnl[i];
    if (equity > peak) {
      peak = equity;
    }
    const drawdown = peak - equity;
    if (drawdown > maxDrawdown) {
      maxDrawdown = drawdown;
      if (initialBalance > 0) {
        maxDrawdownPercent = (drawdown / initialBalance) * 100; // Calculate as % of initial balance
      }
    }
  }
  
  // Profit factor
  const totalWins = wins.reduce((sum, t) => sum + Math.abs(t.pnl), 0);
  const totalLosses = losses.reduce((sum, t) => sum + Math.abs(t.pnl), 0);
  const profitFactor = totalLosses > 0 ? totalWins / totalLosses : totalWins > 0 ? Infinity : 0;
  
  // Sharpe ratio (simplified - using daily returns)
  const sharpeRatio = pnlStdDev > 0 ? (avgPnl / pnlStdDev) * Math.sqrt(252) : 0; // Annualized
  
  // Risk/Reward analysis
  const riskRewards = trades
    .filter(t => t.riskRewardRatio)
    .map(t => t.riskRewardRatio);
  const avgRiskReward = riskRewards.length > 0
    ? riskRewards.reduce((sum, rr) => sum + rr, 0) / riskRewards.length
    : 0;
  
  // Trading style identification
  let tradingStyle = 'Unknown';
  if (avgDuration < 5) {
    tradingStyle = 'Scalping';
  } else if (avgDuration < 60) {
    tradingStyle = 'Day Trading';
  } else if (avgDuration < 1440) { // 24 hours
    tradingStyle = 'Intraday Swing';
  } else {
    tradingStyle = 'Swing Trading';
  }
  
  // Consecutive wins/losses
  let currentWinStreak = 0;
  let currentLossStreak = 0;
  let maxWinStreak = 0;
  let maxLossStreak = 0;
  
  trades.forEach(trade => {
    if (trade.pnl >= 0) {
      currentWinStreak++;
      currentLossStreak = 0;
      maxWinStreak = Math.max(maxWinStreak, currentWinStreak);
    } else {
      currentLossStreak++;
      currentWinStreak = 0;
      maxLossStreak = Math.max(maxLossStreak, currentLossStreak);
    }
  });
  
  // Best and worst trades
  const bestTrade = trades.reduce((best, trade) => 
    (!best || trade.pnl > best.pnl) ? trade : best, null);
  const worstTrade = trades.reduce((worst, trade) => 
    (!worst || trade.pnl < worst.pnl) ? trade : worst, null);
  
  // Daily performance
  const dailyPnl = {};
  trades.forEach(trade => {
    const date = new Date(trade.closeDate).toDateString();
    if (!dailyPnl[date]) {
      dailyPnl[date] = 0;
    }
    dailyPnl[date] += trade.pnl;
  });
  
  const winningDays = Object.values(dailyPnl).filter(pnl => pnl > 0).length;
  const losingDays = Object.values(dailyPnl).filter(pnl => pnl < 0).length;
  
  // Market condition analysis
  const marketConditions = trades
    .filter(t => t.marketCondition)
    .reduce((acc, t) => {
      acc[t.marketCondition] = (acc[t.marketCondition] || 0) + 1;
      return acc;
    }, {});
  
  // Setup quality analysis
  const setupQualities = trades
    .filter(t => t.setupQuality)
    .reduce((acc, t) => {
      if (!acc[t.setupQuality]) {
        acc[t.setupQuality] = { count: 0, totalPnl: 0, winRate: 0 };
      }
      acc[t.setupQuality].count++;
      acc[t.setupQuality].totalPnl += t.pnl;
      return acc;
    }, {});
  
  // Calculate win rate for each setup quality
  Object.keys(setupQualities).forEach(quality => {
    const qualityTrades = trades.filter(t => t.setupQuality === quality);
    const qualityWins = qualityTrades.filter(t => t.pnl >= 0).length;
    setupQualities[quality].winRate = (qualityWins / qualityTrades.length) * 100;
  });
  
  // Tag performance analysis
  const tagPerformance = {};
  trades.forEach(trade => {
    if (trade.tags && Array.isArray(trade.tags)) {
      trade.tags.forEach(tag => {
        if (!tagPerformance[tag]) {
          tagPerformance[tag] = { count: 0, totalPnl: 0, winRate: 0 };
        }
        tagPerformance[tag].count++;
        tagPerformance[tag].totalPnl += trade.pnl;
      });
    }
  });
  
  // Calculate win rate for each tag
  Object.keys(tagPerformance).forEach(tag => {
    const tagTrades = trades.filter(t => t.tags && t.tags.includes(tag));
    const tagWins = tagTrades.filter(t => t.pnl >= 0).length;
    tagPerformance[tag].winRate = tagTrades.length > 0 ? (tagWins / tagTrades.length) * 100 : 0;
  });
  
  return {
    // Basic metrics
    totalPnl,
    winRate,
    avgDuration,
    avgPoints,
    avgPnl,
    totalTrades: trades.length,
    wins: wins.length,
    losses: losses.length,
    
    // Risk metrics
    qtyStd,
    maxDrawdown,
    maxDrawdownPercent,
    profitFactor,
    sharpeRatio,
    avgRiskReward,
    
    // Pattern analysis
    lowPnlCount,
    scalpCount,
    zeroFeeSwap,
    consecutiveWins: maxWinStreak,
    consecutiveLosses: maxLossStreak,
    
    // Performance metrics
    cumulativePnl,
    bestTrade,
    worstTrade,
    winningDays,
    losingDays,
    
    // Style and conditions
    tradingStyle,
    marketConditions,
    setupQualities,
    tagPerformance,
    
    // Additional insights
    avgWinAmount: wins.length > 0 ? totalWins / wins.length : 0,
    avgLossAmount: losses.length > 0 ? -totalLosses / losses.length : 0,
    expectancy: avgPnl,
    kellyPercentage: winRate > 0 && profitFactor > 1 ? 
      ((winRate / 100) * profitFactor - (1 - winRate / 100)) / profitFactor : 0,
  };
};

// Position size calculator based on risk
export const calculatePositionSize = (accountBalance, riskPercent, entryPrice, stopLoss) => {
  const riskAmount = accountBalance * (riskPercent / 100);
  const pointsRisk = Math.abs(entryPrice - stopLoss);
  const positionSize = riskAmount / pointsRisk;
  
  return {
    positionSize: positionSize.toFixed(2),
    riskAmount: riskAmount.toFixed(2),
    pointsRisk: pointsRisk.toFixed(2),
  };
};

// Format currency
export const formatCurrency = (value) => {
  if (value === undefined || value === null) {
    return '$0.00';
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(value);
};

// Format percentage
export const formatPercent = (value) => {
  if (value === undefined || value === null) {
    return '0.00%';
  }
  return `${value.toFixed(2)}%`;
};

// Get trade duration in readable format
export const formatDuration = (minutes) => {
  if (minutes < 60) {
    return `${Math.round(minutes)}m`;
  } else if (minutes < 1440) {
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    return `${hours}h ${mins}m`;
  } else {
    const days = Math.floor(minutes / 1440);
    const hours = Math.floor((minutes % 1440) / 60);
    return `${days}d ${hours}h`;
  }
};

// Function to identify impulsive trades based on keywords in notes
export const identifyImpulsiveTrades = (trades) => {
  // Common words/phrases that might indicate impulsive trading
  const impulsiveKeywords = [
    'impulse', 'impulsive', 'sudden', 'quick', 'rushed', 'hasty',
    'spontaneous', 'without thinking', 'emotional', 'frustrated',
    'angry', 'revenge', 'bored', 'addicted', 'couldn\'t help',
    'felt like', 'just because', 'on a whim', 'whimsical', 'sharp move', 'volitile',
    'news', 'don\'t know', 'left', 'FOMO', 'fomo'
  ];
  
  return trades.filter(trade => {
    if (!trade.notes) return false;
    
    const notes = trade.notes.toLowerCase();
    return impulsiveKeywords.some(keyword => notes.includes(keyword));
  });
};